import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Search,
  FilterList,
  ShoppingCart,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  CalendarToday,
  AttachMoney,
  Timeline,
  FileDownload,
  PlayArrow,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { MobileFAB } from '../../components/Mobile'
import {
  fetchOrders,
  setFilters,
  deleteOrder,
  updateOrderStatus,
} from '../../store/slices/ordersSlice'
import { useOrderUpdates } from '../../hooks/useWebSocket'
import { usePullToRefresh } from '../../hooks/useSwipeGesture'
import { createStaggerAnimation } from '../../utils/mobileAnimations'
import { OrderDialog } from '../../components/Orders/OrderDialog'
import { OrderDetailsDialog } from '../../components/Orders/OrderDetailsDialog'
import { ConfirmDialog } from '../../components/Common/ConfirmDialog'

const ORDER_STATUSES = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

/**
 * Orders Mobile View
 * Card-based order list optimized for mobile
 */
export const OrdersMobileView: React.FC = () => {
  const dispatch = useAppDispatch()
  const { orders, total, isLoading, filters, pagination } = useAppSelector((state) => state.orders)
  const { orders: realtimeOrders } = useOrderUpdates()

  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '')
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuOrder, setMenuOrder] = useState<any>(null)

  // Pull to refresh
  const { handlers, isPulling, isRefreshing } = usePullToRefresh(async () => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    await dispatch(fetchOrders(params))
  })

  // Load orders
  useEffect(() => {
    const params = {
      skip: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filters,
    }
    dispatch(fetchOrders(params))
  }, [dispatch, filters, pagination])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    setTimeout(() => {
      dispatch(setFilters({ search: value }))
    }, 500)
  }

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status)
    dispatch(setFilters({ status: status || undefined }))
  }

  const handleCreateOrder = () => {
    setSelectedOrder(null)
    setOrderDialogOpen(true)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, order: any) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setMenuOrder(order)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuOrder(null)
  }

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order)
    setDetailsDialogOpen(true)
    handleMenuClose()
  }

  const handleEditOrder = (order: any) => {
    setSelectedOrder(order)
    setOrderDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteOrder = (order: any) => {
    setSelectedOrder(order)
    setConfirmDialogOpen(true)
    handleMenuClose()
  }

  const confirmDelete = async () => {
    if (selectedOrder) {
      await dispatch(deleteOrder(selectedOrder.order_id))
      setConfirmDialogOpen(false)
      setSelectedOrder(null)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'pending':
        return 'info'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'error'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  return (
    <Box
      sx={{ pb: 10, bgcolor: '#f5f7fa', minHeight: '100vh' }}
      onTouchStart={handlers.onTouchStart as any}
      onTouchMove={handlers.onTouchMove as any}
      onTouchEnd={handlers.onTouchEnd as any}
    >
      {/* Pull to Refresh Indicator */}
      {isPulling && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            zIndex: 1000,
          }}
        >
          <Typography variant="caption">
            {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
          </Typography>
        </Box>
      )}

      {/* Search and Filters */}
      <Box sx={{ px: 3, pt: 2, mb: 2 }}>
        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search orders..."
          value={searchTerm}
          onChange={handleSearch}
          size="small"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '16px',
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              '& fieldset': {
                border: 'none',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Status Filter Chips */}
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 2 }}>
          {ORDER_STATUSES.map((status) => (
            <Chip
              key={status.value}
              label={status.label}
              onClick={() => handleStatusFilter(status.value)}
              sx={{
                borderRadius: '20px',
                fontWeight: 600,
                fontSize: '0.813rem',
                px: 1,
                bgcolor: selectedStatus === status.value ? '#ff6b6b' : 'white',
                color: selectedStatus === status.value ? 'white' : 'text.primary',
                border: selectedStatus === status.value ? 'none' : '1px solid #e0e0e0',
                boxShadow: selectedStatus === status.value ? '0 2px 8px rgba(255, 107, 107, 0.3)' : 'none',
                '&:hover': {
                  bgcolor: selectedStatus === status.value ? '#ee5a52' : '#f5f5f5',
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Orders List */}
      <Box sx={{ px: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
          Active Orders
        </Typography>
        {isLoading && orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Loading orders...</Typography>
          </Box>
        ) : orders && orders.length > 0 ? (
          <Stack spacing={2.5}>
            {orders.map((order: any, index: number) => (
              <Card
                key={order.order_id}
                onClick={() => handleViewOrder(order)}
                sx={{
                  borderRadius: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  border: 'none',
                  overflow: 'hidden',
                  bgcolor: 'white',
                  ...createStaggerAnimation(index, 30),
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                {/* Orange/Coral Gradient Accent Bar */}
                <Box
                  sx={{
                    height: 6,
                    background: 'linear-gradient(90deg, #ff6b6b 0%, #ffa500 100%)',
                  }}
                />
                
                <CardContent sx={{ p: 2.5 }}>
                  {/* Header with Order Number */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                      }}
                    >
                      <ShoppingCart sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        #{order.order_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {order.customer_name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, order)}
                      sx={{
                        bgcolor: '#f5f5f5',
                        '&:hover': {
                          bgcolor: '#e0e0e0',
                        },
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* Product Info Badge */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 2,
                      py: 0.75,
                      bgcolor: '#fff5f5',
                      borderRadius: '20px',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color="#ff6b6b">
                      {order.product_type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {order.quantity} pcs
                    </Typography>
                  </Box>

                  {/* Details Grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                      mb: 2,
                      p: 2,
                      bgcolor: '#f8f9fa',
                      borderRadius: '12px',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Amount
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="#ff6b6b">
                        Rs. {order.total_amount?.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Due Date
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(order.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Status and Priority Row */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      label={order.status}
                      size="small"
                      sx={{
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor:
                          order.status?.toLowerCase() === 'completed'
                            ? '#e8f5e9'
                            : order.status?.toLowerCase() === 'in_progress'
                            ? '#fff3e0'
                            : order.status?.toLowerCase() === 'pending'
                            ? '#e3f2fd'
                            : '#ffebee',
                        color:
                          order.status?.toLowerCase() === 'completed'
                            ? '#2e7d32'
                            : order.status?.toLowerCase() === 'in_progress'
                            ? '#f57c00'
                            : order.status?.toLowerCase() === 'pending'
                            ? '#1976d2'
                            : '#c62828',
                        border: 'none',
                      }}
                    />
                    <Chip
                      label={order.priority}
                      size="small"
                      sx={{
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor:
                          order.priority?.toLowerCase() === 'urgent'
                            ? '#ffebee'
                            : order.priority?.toLowerCase() === 'high'
                            ? '#fff3e0'
                            : order.priority?.toLowerCase() === 'medium'
                            ? '#e3f2fd'
                            : '#e8f5e9',
                        color:
                          order.priority?.toLowerCase() === 'urgent'
                            ? '#c62828'
                            : order.priority?.toLowerCase() === 'high'
                            ? '#f57c00'
                            : order.priority?.toLowerCase() === 'medium'
                            ? '#1976d2'
                            : '#2e7d32',
                        border: 'none',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Card sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: '20px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <ShoppingCart sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom fontWeight={600}>
              No orders found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || selectedStatus
                ? 'Try adjusting your filters'
                : 'Create your first order to get started'}
            </Typography>
          </Card>
        )}
      </Box>

      {/* Floating Action Button */}
      <Box
        onClick={handleCreateOrder}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          bgcolor: '#ff6b6b',
          color: 'white',
          borderRadius: '20px',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 8px 24px rgba(255, 107, 107, 0.4)',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:active': {
            transform: 'scale(0.95)',
          },
        }}
      >
        <ShoppingCart />
        <Typography variant="body2" fontWeight={600}>
          New Order
        </Typography>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={() => menuOrder && handleViewOrder(menuOrder)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuOrder && handleEditOrder(menuOrder)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Order</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <Timeline fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Progress</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuOrder && handleDeleteOrder(menuOrder)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <OrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        order={selectedOrder}
      />

      <OrderDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        order={selectedOrder}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Order"
        message={`Are you sure you want to delete order ${selectedOrder?.order_number}? This action cannot be undone.`}
      />
    </Box>
  )
}
